import type {
  FormatOptionLabelMeta,
  Props as ReactSelectProps,
} from 'react-select';
import ReactSelect from 'react-select';

import { useI18n } from '#/i18n/hooks/useI18n';

import { ClearIndicator } from './ClearIndicator';
import { DropdownIndicator } from './DropdownIndicator';
import { MultiValueRemove } from './MultiValueRemove';
import { multiSelectStyles } from './Select.styled';
import type { OptionType } from './Select.types';

type Props<T> = {
  name?: string;
  value: OptionType<T>['value'][] | null;
  options: OptionType<T>[];
  disabled?: boolean;
  onChange: (value: OptionType<T>['value'][]) => void;
  onFocus?: () => void;
  maxSelected?: number | null | undefined;
  placeholder?: string;
  label?: string;
  id?: string;
  qaId?: string;
  menuPortalTarget?: ReactSelectProps['menuPortalTarget'];
  menuPosition?: ReactSelectProps['menuPosition'];
  formatOptionLabel?: (
    option: OptionType<T>,
    meta: FormatOptionLabelMeta<OptionType<T>>,
  ) => React.ReactNode;
};

export function MultiSelect<T = string>({
  name,
  value,
  options,
  disabled,
  onChange,
  maxSelected = 5,
  label,
  placeholder,
  onFocus,
  menuPortalTarget,
  menuPosition,
  qaId,
  id,
  formatOptionLabel,
}: Props<T>) {
  const { i18n } = useI18n();

  const isSearchable = !value || !maxSelected || value.length < maxSelected;
  let curOptions = options;
  if (!isSearchable) {
    curOptions = options.filter((option) => value.includes(option.value));
    curOptions.push({
      value: 'MAX_SELECTED',
      label: i18n(
        'You have reached the maximum number of options you can select',
      ),
    } as OptionType<T>);
  }

  // preserve order of user selection
  const curValue = value
    ? value
        .map((v) => options.find((option) => option.value === v))
        .filter(Boolean)
    : [];

  return (
    <ReactSelect<OptionType<T>, true>
      components={{
        DropdownIndicator,
        ClearIndicator,
        MultiValueRemove,
      }}
      classNamePrefix="react-select"
      maxMenuHeight={250}
      aria-label={label}
      inputId={id}
      name={name}
      options={curOptions}
      value={curValue}
      isDisabled={disabled}
      isOptionDisabled={(option: OptionType<T>) =>
        option.value === 'MAX_SELECTED' || option.isDisabled === true
      }
      // oxlint-disable-next-line typescript/no-explicit-any
      onChange={(newValues: any) => {
        // oxlint-disable-next-line typescript/no-explicit-any
        onChange(newValues.map((v: any) => v.value));
      }}
      placeholder={placeholder ?? i18n('Select...')}
      isSearchable={isSearchable}
      isClearable
      isMulti
      className={qaId ? `qa-id-${qaId}` : undefined}
      styles={multiSelectStyles<OptionType<T>, true>()}
      menuPortalTarget={menuPortalTarget}
      menuPosition={menuPosition}
      onFocus={onFocus}
      formatOptionLabel={formatOptionLabel}
    />
  );
}
